import * as Sentry from '@sentry/browser'
import {DonationPurpose, Metadata} from 'components/DonationBox/shared'
import HumanFeedbackError from 'lib/HumanFeedbackError'
import {useState, useEffect} from 'react'
import env from 'lib/env'
import {Locale} from 'i18n'
import ky from 'ky'

function isAPIPayPalResponse(res: unknown): res is {approval_url: string} {
  return typeof res === 'object' && !!res && 'approval_url' in res
}
function isAPIPayPalConfirmation(res: unknown): res is {paid: boolean} {
  return typeof res === 'object' && !!res && 'paid' in res
}

const DONATION_API = env.GATSBY_DONATION_API

interface IInitiatePaymentArgs {
  amount: number
  firstName: string
  lastName: string
  email: string
  donationPurpose: DonationPurpose
  metadata: Metadata | null
  isRecurrent: boolean
  language: Locale
  recaptcha: string
  ansar: boolean
}

const usePayPalPayment = ({
  onPaymentCompleted,
}: {
  onPaymentCompleted: (success: boolean) => void
}) => {
  const [loading, setLoading] = useState(false)
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search)
    const paymentId = urlParams.get('paymentId')
    const token = urlParams.get('token')
    const payerId = urlParams.get('PayerID')
    const subscriptionId = urlParams.get('subscription_id')
    const baToken = urlParams.get('ba_token')
    if (subscriptionId && baToken) {
      // recurrent payment
      onPaymentCompleted(true)
      window.history.replaceState(null, '', window.location.pathname)
    } else if (paymentId && payerId) {
      // one time payment
      ;(async function processPayPalPayment() {
        try {
          setLoading(true)
          const endpoint = `${DONATION_API}donate_once/paypal/verify/`
          const response: unknown = await ky(endpoint, {
            method: 'POST',
            json: {
              payment_id: paymentId,
              payer_id: payerId,
            },
          }).then((response) => response.json())
          if (isAPIPayPalConfirmation(response) && response.paid === true) {
            onPaymentCompleted(true)
          } else {
            console.error({response})
            throw new Error(
              'An error has occurred while confirming the payment',
            )
          }
        } catch (e) {
          console.error(e)
          Sentry.captureException(e)
          // TODO: send params to sentry
          onPaymentCompleted(false)
        } finally {
          window.history.replaceState(null, '', window.location.pathname)
          setLoading(false)
        }
      })()
    } else if (token) {
      Sentry.captureMessage(
        'Got a token but not other valid params' + window.location.search,
      )
      onPaymentCompleted(false)
      window.history.replaceState(null, '', window.location.pathname)
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps
  const initiatePayment = async ({
    amount,
    firstName,
    lastName,
    email,
    donationPurpose,
    metadata,
    isRecurrent,
    language,
    recaptcha,
    ansar
  }: IInitiatePaymentArgs): Promise<{success: boolean; message: string}> => {
    try {
      setLoading(true)
      const orderData = {
        amount: amount,
        first_name: firstName,
        last_name: lastName,
        email: email,
        charity_organization: donationPurpose,
        return_url: window.location.href,
        cancel_url: window.location.href,
        metadata,
        language,
        recaptcha,
        support_ansar: ansar
      }
      const endpoint = isRecurrent
        ? `${DONATION_API}recurrent_donation/paypal`
        : `${DONATION_API}donate_once/paypal`
      const response: unknown = await ky(endpoint, {
        method: 'POST',
        json: orderData,
      }).then((result) => result.json())

      if (!isAPIPayPalResponse(response)) {
        throw new Error(
          response
            ? JSON.stringify(response)
            : "An unknown error from our API has occurred on endpoint 'donate_once'",
        )
      }

      window.location.href = response.approval_url

      return {success: true, message: 'Redirecting to paypal'}
    } catch (e) {
      // TODO: attach user info for sentry
      // https://docs.sentry.io/enriching-error-data/context/?platform=browser
      console.error(e)
      Sentry.captureException(e)
      setLoading(false)
      if (e instanceof HumanFeedbackError) {
        return {
          success: false,
          message: e.humanMessage,
        }
      } else if (e instanceof Error) {
        return {
          success: false,
          message:
            'An error has occurred, we will investigate. Please try again and contact us if it persist',
        }
      } else {
        throw e
      }
    }
  }
  return {loading, initiatePayment}
}

export default usePayPalPayment
